import React, { useState } from 'react';
import { Check, Users, Luggage, Car } from 'lucide-react';
import { useLanguage } from '../locales/LanguageContext';

// Map of vehicle name keywords to local fallback images
const FALLBACK_IMAGES = {
  sedan: '/images/vehicles/sedan.svg',
  suv: '/images/vehicles/suv.svg',
  van: '/images/vehicles/van.svg',
  minibus: '/images/vehicles/minibus.svg',
  bus: '/images/vehicles/minibus.svg',
};
const DEFAULT_FALLBACK = '/images/vehicles/default.svg';

/**
 * Returns local fallback image based on vehicle name keywords.
 */
const getFallbackImage = (vehicleName = '') => {
  const lower = vehicleName.toLowerCase();
  for (const [keyword, src] of Object.entries(FALLBACK_IMAGES)) {
    if (lower.includes(keyword)) return src;
  }
  return DEFAULT_FALLBACK;
};

export const VehicleCard = ({ vehicle, onSelect, isSelected }) => {
  const { t } = useLanguage();
  const hasExternalImage = vehicle.image_url && vehicle.image_url.startsWith('http');
  const [imageError, setImageError] = useState(false);
  
  const displayImage = (hasExternalImage && !imageError)
    ? vehicle.image_url
    : getFallbackImage(vehicle.name);
  
  return (
    <div 
      className={`
        group relative bg-white border rounded-lg sm:rounded-xl overflow-hidden 
        transition-all duration-300 cursor-pointer
        ${isSelected 
          ? 'border-navy-500 shadow-lg ring-2 ring-navy-200' 
          : 'border-slate-200 hover:border-navy-300 hover:shadow-md'}
      `}
      onClick={onSelect}
      data-testid={`vehicle-card-${vehicle.vehicle_type_id || vehicle.id}`}
    >
      {isSelected && (
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10 bg-navy-800 text-white rounded-full p-1">
          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </div>
      )}
      
      <div className="aspect-video overflow-hidden bg-slate-100 flex items-center justify-center">
        <img 
          src={displayImage} 
          alt={vehicle.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => {
            if (!imageError) setImageError(true);
          }}
          loading="lazy"
        />
      </div>
      
      <div className="p-3 sm:p-4 md:p-5">
        <h3 className="font-manrope font-bold text-lg sm:text-xl text-navy-950 mb-2">
          {vehicle.name}
        </h3>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3 text-xs sm:text-sm text-slate-600">
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
            <span className="whitespace-nowrap">{t('vehicleCard.up_to')} {vehicle.capacity_max} {t('vehicleCard.passengers')}</span>
          </div>
          {vehicle.luggage_max && (
            <div className="flex items-center gap-1">
              <Luggage className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
              <span className="whitespace-nowrap">{vehicle.luggage_max} {t('vehicleCard.luggage')}</span>
            </div>
          )}
        </div>
        
        {vehicle.features && vehicle.features.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {vehicle.features.slice(0, 3).map((feature, idx) => (
              <span 
                key={idx}
                className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-indigo-50 text-navy-800 rounded-md"
              >
                {feature}
              </span>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500">{t('vehicleCard.from_price')}</p>
            <p className="text-xl sm:text-2xl font-bold text-navy-800">
              ${Number(vehicle.price).toFixed(2)}
              <span className="text-xs sm:text-sm font-normal text-slate-500"> {vehicle.currency}</span>
            </p>
          </div>
          <button 
            className={`
              px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors
              ${isSelected 
                ? 'bg-navy-800 text-white' 
                : 'bg-indigo-50 text-navy-800 hover:bg-indigo-100'}
            `}
            data-testid={`select-vehicle-${vehicle.vehicle_type_id || vehicle.id}`}
          >
            <span className="whitespace-nowrap">{isSelected ? t('vehicleCard.selected') : t('vehicleCard.select')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
